import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Box, Paper, Chip, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { CloudUploadOutlined as CloudUploadIcon, Check as CheckIcon, PriorityHigh as WarnIcon, InfoOutlined as InfoIcon, Remove as DashIcon } from '@material-ui/icons'
import { Toast } from 'components/shared/Toaster'
import Loader from 'components/shared/Loader'
import Button from 'components/shared/Button'
import ReportEmptyState from 'components/shared/ReportEmptyState'
import CreditCost from 'components/shared/CreditCost'
import strings from 'strings'
import { findCreditCost } from 'tools/helper'
import { getLastCashFlowStatement, uploadCashFlowStatement } from '../../../../../actions/applicants'
import { ACCENT_PURPLE, BANNER_BG, SEVERITY_STYLES } from '../IncomeStatement/incomeStatementAdapters'
import {
  conceptText, deriveFlags, deriveKeyIndicators, deriveStructure, deriveSummary, formatOptionalCurrency, formatPeriodRange,
  getStatusStyle, methodText, readStatement, scaleText, scopeText
} from './cashFlowAdapters'
import { reportStyles } from '../shared/reportStyles'

const ACCEPTED_FILES = '.pdf,.jpg,.jpeg,.png,.csv,.xls,.xlsx'
const MAX_BANNER_OBSERVATIONS = 3

const useStyles = makeStyles((theme) => ({
  ...reportStyles(theme),
  periodItem: { flex: '1 1 220px', minWidth: 200, border: '1px solid #ECECEC', borderRadius: '10px', padding: theme.spacing(1.5) },
  railButton: { borderRadius: '8px', padding: '10px 16px', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#fff', color: '#333', border: '1px solid #ddd', cursor: 'pointer' },
  banner: { backgroundColor: BANNER_BG, borderRadius: '14px', padding: theme.spacing(4), color: '#fff', display: 'flex', justifyContent: 'space-between', gap: theme.spacing(3), [theme.breakpoints.down('xs')]: { flexDirection: 'column' } },
  tilesRow: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2) },
  tileValue: { fontSize: 24, fontWeight: 700, textAlign: 'center', margin: '8px 0' },
  tileNote: { fontSize: 11, color: '#777', textAlign: 'center' },
  twoColRow: { display: 'flex', gap: theme.spacing(3), flexWrap: 'wrap' },
  colWide: { flex: '1.4 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: theme.spacing(3) },
  colNarrow: { flex: '1 1 320px', minWidth: 0 },
  sectionCard: { padding: theme.spacing(3), borderRadius: '12px', border: '1px solid #ECECEC', boxShadow: 'none' },
  sectionTitle: { fontWeight: 700, fontSize: 15 },
  sectionSubtitle: { fontSize: 12, color: '#777' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 8 },
  th: { textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--neutral-400)', padding: '6px 8px', borderBottom: '1px solid #ECECEC' },
  td: { padding: '8px 8px', borderBottom: '1px solid #F1F1F1', fontSize: 13, verticalAlign: 'top' },
  right: { textAlign: 'right', whiteSpace: 'nowrap' },
  typeChip: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, backgroundColor: '#F1F1F1', fontSize: 11, fontWeight: 600 },
  muted: { color: '#999', fontSize: 11 },
  checkRow: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', fontSize: 13 },
  checkIcon: { fontSize: 16, marginTop: 2 },
  checkNote: { display: 'block', fontSize: 11, color: '#999' },
  flagGrid: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 },
  flagRow: { borderBottom: '1px solid #F1F1F1', paddingBottom: 10 },
  flagHead: { display: 'flex', flexDirection: 'column', gap: 6 },
  severityChip: { alignSelf: 'flex-start', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' },
}))

const CHECK_ICON_COLORS = { PASS: '#2c8f68', DERIVED: '#9a9a9a', DOUBT: ACCENT_PURPLE, WARNING: 'var(--status-pending)', NOT_APPLICABLE: '#bbb' }

const CheckStatusIcon = ({ status, className }) => {
  const style = { color: CHECK_ICON_COLORS[status] || CHECK_ICON_COLORS.WARNING }
  if (status === 'PASS' || status === 'DERIVED') return <CheckIcon className={className} style={style} data-status={status} />
  if (status === 'DOUBT') return <InfoIcon className={className} style={style} data-status={status} />
  if (status === 'NOT_APPLICABLE') return <DashIcon className={className} style={style} data-status={status} />
  return <WarnIcon className={className} style={style} data-status={status} />
}

CheckStatusIcon.propTypes = { status: PropTypes.string, className: PropTypes.string }

const confidenceTone = (confidence) => (confidence === 'HIGH' ? 'Positive' : confidence === 'MEDIUM' ? 'Medium' : 'High')

const DisclosureTable = ({ classes, items }) => (
  <table className={classes.table}>
    <tbody>
      {items.map((item) => (
        <tr key={item.key}>
          <td className={classes.td} style={{ paddingLeft: 0 }}>
            {item.rawLabel}
            {item.normalizedKey && <span className={classes.muted}> · {conceptText(item.normalizedKey)}</span>}
            {item.page && <span className={classes.muted}> · {strings.CASH_FLOW_PAGE_PREFIX} {item.page}</span>}
          </td>
          <td className={`${classes.td} ${classes.right}`} style={{ paddingRight: 0 }}>{formatOptionalCurrency(item.value)}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

DisclosureTable.propTypes = { classes: PropTypes.object.isRequired, items: PropTypes.array.isRequired }

const CashFlowStatement = ({ applicationId, creditCosts }) => {
  const classes = useStyles()
  const [initialLoading, setInitialLoading] = useState(true)
  const [doc, setDoc] = useState(null)
  const [uploading, setUploading] = useState(false)

  const documentAnalysisCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')

  const fetchDoc = useCallback(async () => {
    const latest = await getLastCashFlowStatement(applicationId)().catch(() => null)
    setDoc(latest && typeof latest === 'object' && Object.keys(latest).length > 0 ? latest : null)
  }, [applicationId])

  useEffect(() => {
    if (!applicationId) {
      setInitialLoading(false)
      return
    }
    fetchDoc().finally(() => setInitialLoading(false))
  }, [applicationId, fetchDoc])

  const handleUpload = async (event) => {
    const file = event.target.files[0]
    event.target.value = ''
    if (!file || !applicationId) return
    setUploading(true)
    try {
      const res = await uploadCashFlowStatement(applicationId, file)()
      Toast({ type: 'success', message: strings.CASH_FLOW_UPLOAD_SUCCESS })
      if (res && typeof res === 'object') setDoc(res)
      else await fetchDoc()
    } catch (err) {
      Toast({ type: 'error', message: strings.CASH_FLOW_UPLOAD_ERROR })
    } finally {
      setUploading(false)
    }
  }

  const statement = useMemo(() => (doc ? readStatement(doc) : null), [doc])
  const period = useMemo(() => (statement ? statement.periods.find((p) => p.periodId === statement.latestPeriodId) || statement.periods[0] || null : null), [statement])
  const insights = statement ? statement.insights : null
  const summary = useMemo(() => (insights ? deriveSummary(insights) : null), [insights])
  const statusStyle = getStatusStyle(summary ? summary.statusLabel : null)
  const indicators = useMemo(() => (insights ? deriveKeyIndicators(insights) : []), [insights])
  const flags = useMemo(() => (insights ? deriveFlags(insights) : []), [insights])
  const structure = useMemo(() => (period ? deriveStructure(period) : null), [period])

  const uploadInput = (id) => <input accept={ACCEPTED_FILES} style={{ display: 'none' }} id={id} type="file" onChange={handleUpload} />

  if (initialLoading) {
    return <Box className={classes.container}><Loader /></Box>
  }

  if (uploading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.centeredState}>
          <CircularProgress size={48} thickness={4} style={{ color: BANNER_BG, marginBottom: 24 }} />
          <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>{strings.CASH_FLOW_PROCESSING_TITLE}</Typography>
          <Typography variant="body1" style={{ color: '#666' }}>{strings.CASH_FLOW_PROCESSING_MESSAGE}</Typography>
        </Box>
      </Box>
    )
  }

  if (!statement) {
    return (
      <Box className={classes.container}>
        {uploadInput('cash-flow-upload-empty')}
        <ReportEmptyState
          title={strings.CASH_FLOW_NO_REPORTS_TITLE}
          description={strings.CASH_FLOW_NO_REPORTS_MESSAGE}
          action={
            <Button size="large" style={{ padding: '12px 32px', fontSize: '1rem' }} onClick={() => document.getElementById('cash-flow-upload-empty')?.click()}>
              {strings.CASH_FLOW_UPLOAD_BUTTON} <CreditCost cost={documentAnalysisCreditCost()} />
            </Button>
          }
        />
      </Box>
    )
  }

  const uploadControl = (
    <>
      {uploadInput('cash-flow-upload')}
      <label htmlFor="cash-flow-upload">
        <span role="button" tabIndex={0} className={classes.railButton}>
          <CloudUploadIcon style={{ fontSize: 16 }} />
          {strings.CASH_FLOW_UPLOAD_BUTTON} <CreditCost cost={documentAnalysisCreditCost()} />
        </span>
      </label>
    </>
  )

  if (!statement.valid) {
    return (
      <div className={classes.container}>
        <div className={classes.layout}>
          <Alert severity="error">{statement.error || strings.CASH_FLOW_ANALYSIS_FAILED}</Alert>
          <div>{uploadControl}</div>
        </div>
      </div>
    )
  }

  const warnings = [...statement.warnings, ...(period ? period.warnings.filter((w) => !statement.warnings.includes(w)) : [])]
  const bannerObservations = insights ? insights.observations.slice(0, MAX_BANNER_OBSERVATIONS) : []
  const periodRange = period ? formatPeriodRange(period.startDate, period.endDate) : ''

  return (
    <div className={classes.container}>
      <div className={classes.layout}>
        {/* Rail: one card per upload, as the income statement */}
        <Paper className={classes.sidebarCard}>
          <div className={classes.sidebarHeader}>
            <div className={classes.sidebarHeaderLeft}>
              <Typography style={{ fontWeight: 700, fontSize: 16 }}>{strings.CASH_FLOW_STATEMENTS}</Typography>
              <Typography className={classes.periodMeta}>
                {statement.periodsOnStatement} {strings.CASH_FLOW_PERIODS_SUFFIX}{statement.periodsOnStatement > statement.periods.length ? ` · ${strings.CASH_FLOW_LAST_YEAR_SHOWN}` : ''}
              </Typography>
              <Chip size="small" className={classes.statusChip} label={methodText(statement.operatingMethod)} />
              {statement.currency && <Chip size="small" className={classes.statusChip} label={`${statement.currency} · ${scaleText(statement.scale)}`} />}
              {statement.consolidated && <Chip size="small" className={classes.statusChip} label={strings.CASH_FLOW_CONSOLIDATED} />}
              {statement.auditStatus && <Chip size="small" className={classes.statusChip} label={statement.auditStatus} />}
              {statement.confidence && (
                <Chip
                  size="small"
                  className={classes.statusChip}
                  label={`${strings.CASH_FLOW_CONFIDENCE} ${statement.confidence}`}
                  style={{ backgroundColor: SEVERITY_STYLES[confidenceTone(statement.confidence)].bg, color: SEVERITY_STYLES[confidenceTone(statement.confidence)].fg }}
                />
              )}
            </div>
            <div>{uploadControl}</div>
          </div>
          <div className={classes.periodsRow}>
            {period && (
              <div className={`${classes.periodItem} ${classes.periodItemActive}`}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography className={classes.periodDate}>{period.label} · {periodRange}</Typography>
                  <Typography className={classes.periodStatus} style={{ color: statusStyle.accent }}>{summary ? summary.statusLabel : ''}</Typography>
                </Box>
                <Typography className={classes.periodValue}>{formatOptionalCurrency(period.sections.OPERATING.netCash.value)}</Typography>
                <Typography className={classes.periodMeta}>{strings.CASH_FLOW_KPI_OPERATING.toLowerCase()}{statement.entityName ? ` · ${statement.entityName}` : ''}</Typography>
                {insights && insights.healthScore !== null && (
                  <>
                    <Typography className={classes.periodMeta}>
                      {strings.CASH_FLOW_HEALTH} {insights.healthScore.toFixed(1)} / 10 · {strings.CASH_FLOW_KPI_FREE_CASH_FLOW} {formatOptionalCurrency(insights.freeCashFlow)}
                    </Typography>
                    <div className={classes.periodBar}>
                      <div className={classes.periodBarFill} style={{ width: `${Math.min(100, (insights.healthScore / 10) * 100)}%`, backgroundColor: statusStyle.accent }} />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Paper>

        {statement.latestPeriodAmbiguous && <Alert severity="info">{strings.CASH_FLOW_AMBIGUOUS_LATEST}</Alert>}
        {warnings.length > 0 && (
          <Alert severity="warning">
            {warnings.map((text) => <div key={text}>{text}</div>)}
          </Alert>
        )}

        {period && insights && summary && (
          <>
            {/* Verdict banner */}
            <div className={classes.banner}>
              <div style={{ minWidth: 0 }}>
                <Typography className={classes.bannerEyebrow} style={{ color: statusStyle.accent }}>
                  {periodRange.toUpperCase()} · {strings.CASH_FLOW_VERDICT_LABEL}
                </Typography>
                <Typography className={classes.bannerHeadline}>{summary.headline}</Typography>
                {summary.narrative && <Typography className={classes.bannerDescription}>{summary.narrative}</Typography>}
                {summary.bullets.length > 0 && (
                  <ul className={classes.bannerBullets}>
                    {summary.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                  </ul>
                )}
                {bannerObservations.length > 0 && (
                  <>
                    <Typography className={classes.bannerObservationsHead}>{strings.CASH_FLOW_OBSERVATIONS.toUpperCase()}</Typography>
                    <ul className={classes.bannerObservations}>
                      {bannerObservations.map((observation) => (
                        <li key={observation} className={classes.bannerObservation}>
                          <span className={classes.bannerObservationDot} />
                          <span>{observation}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div className={classes.bannerScoreCol}>
                <Typography className={classes.bannerScoreLabel}>{strings.CASH_FLOW_HEALTH_SCORE}</Typography>
                <Typography className={classes.bannerScoreValue} style={{ color: statusStyle.accent }}>
                  {insights.healthScore === null ? '—' : insights.healthScore.toFixed(1)}<span className={classes.bannerScoreUnit}>/10</span>
                </Typography>
                <Chip label={summary.statusLabel} size="small" className={classes.statusChip} style={{ backgroundColor: statusStyle.chipBg, color: statusStyle.chipText }} />
                <div className={classes.dotStrip}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className={classes.dot} style={i < Math.round(insights.healthScore || 0) ? { backgroundColor: statusStyle.accent } : undefined} />
                  ))}
                </div>
                <Typography className={classes.bannerTrend}>{summary.statusText}</Typography>
              </div>
            </div>

            {/* Key indicators */}
            <div className={classes.tilesRow}>
              {indicators.map((kpi) => (
                <Paper className={classes.tile} key={kpi.key} style={kpi.negative ? { borderColor: 'var(--status-pending)' } : undefined}>
                  <Typography className={classes.tileLabel}>{kpi.name.toUpperCase()}</Typography>
                  <Typography className={classes.tileValue} style={kpi.negative ? { color: 'var(--status-pending)' } : undefined}>{kpi.value}</Typography>
                  <Typography className={classes.tileNote}>{kpi.sub}</Typography>
                </Paper>
              ))}
            </div>

            {/* Structure + checks | analysis */}
            <div className={classes.twoColRow}>
              <div className={classes.colWide}>
                <Paper className={classes.sectionCard}>
                  <div className={classes.sectionHead}>
                    <Typography className={classes.sectionTitle}>{strings.CASH_FLOW_STRUCTURE}</Typography>
                    <Typography className={classes.sectionSubtitle}>
                      {periodRange}{scopeText(period.reconciliation.scope) ? `, ${strings.CASH_FLOW_SCOPE_PREFIX.toLowerCase()} ${scopeText(period.reconciliation.scope)}` : ''}
                    </Typography>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.th} style={{ paddingLeft: 0 }}>{strings.CASH_FLOW_STRUCTURE_COLUMN_ITEM.toUpperCase()}</th>
                          <th className={classes.th}>{strings.CASH_FLOW_STRUCTURE_COLUMN_TYPE.toUpperCase()}</th>
                          <th className={classes.th}>{strings.CASH_FLOW_STRUCTURE_COLUMN_CONCEPT.toUpperCase()}</th>
                          <th className={`${classes.th} ${classes.right}`} style={{ paddingRight: 0 }}>{strings.CASH_FLOW_STRUCTURE_COLUMN_AMOUNT.toUpperCase()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {structure.rows.map((row) => (
                          <tr key={`${row.type}-${row.key}`}>
                            <td className={classes.td} style={{ paddingLeft: 0 }}>
                              {row.rawLabel}
                              {row.page && <span className={classes.muted}> · {strings.CASH_FLOW_PAGE_PREFIX} {row.page}</span>}
                            </td>
                            <td className={classes.td}><span className={classes.typeChip}>{row.type}</span></td>
                            <td className={classes.td}><span className={classes.muted}>{row.normalizedKey ? conceptText(row.normalizedKey) : strings.CASH_FLOW_UNMAPPED}</span></td>
                            <td className={`${classes.td} ${classes.right}`} style={{ paddingRight: 0 }}>{formatOptionalCurrency(row.value)}</td>
                          </tr>
                        ))}
                        {structure.totals.map((total) => (
                          <tr key={total.key} style={{ fontWeight: 700 }}>
                            <td className={classes.td} style={{ paddingLeft: 0 }} colSpan={3}>
                              {total.label}
                              {total.figure.reported === null && total.figure.value !== null && <span className={classes.muted}> · {strings.CASH_FLOW_DERIVED}</span>}
                              {total.figure.note && <span className={classes.checkNote}>{total.figure.note}</span>}
                            </td>
                            <td className={`${classes.td} ${classes.right}`} style={{ paddingRight: 0 }}>{total.figure.value === null ? strings.CASH_FLOW_NOT_REPORTED : formatOptionalCurrency(total.figure.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Paper>

                <Paper className={classes.sectionCard}>
                  <Typography className={classes.sectionTitle}>{strings.CASH_FLOW_RECONCILIATION}</Typography>
                  <table className={classes.table}>
                    <tbody>
                      {[
                        [strings.CASH_FLOW_CHANGE_BEFORE_FX, period.reconciliation.netChangeBeforeFx],
                        [strings.CASH_FLOW_FX_EFFECT, period.reconciliation.effectOfExchangeRates],
                        [strings.CASH_FLOW_NET_CHANGE, period.reconciliation.netChangeInCash],
                        [strings.CASH_FLOW_BEGINNING_CASH, period.reconciliation.beginningCash],
                        [strings.CASH_FLOW_ENDING_CASH, period.reconciliation.endingCash]
                      ].map(([label, figure]) => (
                        <tr key={label}>
                          <td className={classes.td} style={{ paddingLeft: 0 }}>
                            {label}
                            {figure.sourceLabel && <span className={classes.muted}> · {figure.sourceLabel}</span>}
                            {figure.reported === null && figure.value !== null && <span className={classes.muted}> · {strings.CASH_FLOW_DERIVED}</span>}
                            {figure.note && <span className={classes.checkNote}>{figure.note}</span>}
                          </td>
                          <td className={`${classes.td} ${classes.right}`} style={{ paddingRight: 0 }}>{figure.value === null ? strings.CASH_FLOW_NOT_REPORTED : formatOptionalCurrency(figure.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Paper>

                <Paper className={classes.sectionCard}>
                  <Typography className={classes.sectionTitle}>{strings.CASH_FLOW_CHECKS_TITLE}</Typography>
                  <Box mt={1}>
                    {period.checks.map((check) => (
                      <div className={classes.checkRow} key={check.key}>
                        <CheckStatusIcon status={check.status} className={classes.checkIcon} />
                        <span style={{ minWidth: 0 }}>
                          <span>{check.label}</span>
                          {check.note && <span className={classes.checkNote}>{check.note}</span>}
                        </span>
                      </div>
                    ))}
                  </Box>
                  {period.validationIssues.length > 0 && (
                    <Box mt={2}>
                      <Typography className={classes.sectionSubtitle}>{strings.CASH_FLOW_ISSUES_TITLE}</Typography>
                      {period.validationIssues.map((issue) => (
                        <div className={classes.checkRow} key={`${issue.rule}-${issue.message}`}>
                          <CheckStatusIcon status={issue.severity === 'INFO' ? 'DOUBT' : issue.severity} className={classes.checkIcon} />
                          <span>{issue.message}</span>
                        </div>
                      ))}
                    </Box>
                  )}
                </Paper>

                {(period.supplementalDisclosures.length > 0 || period.noncashActivities.length > 0 || period.otherLineItems.length > 0) && (
                  <Paper className={classes.sectionCard}>
                    {period.supplementalDisclosures.length > 0 && (
                      <>
                        <Typography className={classes.sectionTitle}>{strings.CASH_FLOW_SUPPLEMENTAL}</Typography>
                        <DisclosureTable classes={classes} items={period.supplementalDisclosures} />
                      </>
                    )}
                    {period.noncashActivities.length > 0 && (
                      <Box mt={period.supplementalDisclosures.length > 0 ? 3 : 0}>
                        <Typography className={classes.sectionTitle}>{strings.CASH_FLOW_NONCASH}</Typography>
                        <DisclosureTable classes={classes} items={period.noncashActivities} />
                      </Box>
                    )}
                    {period.otherLineItems.length > 0 && (
                      <Box mt={3}>
                        <Typography className={classes.sectionTitle}>{strings.CASH_FLOW_OTHER_LINES}</Typography>
                        <DisclosureTable classes={classes} items={period.otherLineItems} />
                      </Box>
                    )}
                  </Paper>
                )}
              </div>

              <div className={classes.colNarrow}>
                <Paper className={classes.sectionCard}>
                  <Typography className={classes.sectionTitle}>{strings.CASH_FLOW_ANALYSIS_TITLE}</Typography>
                  <Typography className={classes.sectionSubtitle}>{strings.CASH_FLOW_ANALYSIS_SUBTITLE}</Typography>
                  <div className={classes.flagGrid}>
                    {flags.length === 0 && <Typography className={classes.sectionSubtitle}>{strings.CASH_FLOW_NO_ANALYSIS}</Typography>}
                    {flags.map((flag) => (
                      <div className={classes.flagRow} key={`${flag.severity}-${flag.line}`}>
                        <div className={classes.flagHead}>
                          <span className={classes.severityChip} style={{ backgroundColor: SEVERITY_STYLES[flag.severity].bg, color: SEVERITY_STYLES[flag.severity].fg }}>{flag.label}</span>
                          <Typography className={classes.flagLine}>{flag.line}</Typography>
                        </div>
                      </div>
                    ))}
                  </div>
                  {statement.documentNotes.length > 0 && (
                    <Box mt={3}>
                      <Typography className={classes.sectionSubtitle} style={{ fontWeight: 700 }}>{strings.CASH_FLOW_DOCUMENT_NOTES}</Typography>
                      {statement.documentNotes.map((note) => <Typography key={note} className={classes.sectionSubtitle}>{note}</Typography>)}
                    </Box>
                  )}
                </Paper>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

CashFlowStatement.propTypes = {
  applicationId: PropTypes.string,
  creditCosts: PropTypes.array
}

export default CashFlowStatement
